<template>
  <v-page-content class="mx-auto" style="max-width: 1024px">
    <div v-if="!editMode">
      <h5 v-if="!isClassEventsPage"><router-link to="/news_feed">Лента новостей</router-link> /</h5>
      <h3>{{ isClassEventsPage ? 'Мероприятия класса' : 'Мероприятия'  }}</h3>
      <div v-if="isClassEventsPageByCurator" class="w-100 mb-2">
        <b-form-select v-if="!isLoading"
          v-model="selectedOrg" :options="optionsOrg"
          v-b-tooltip.hover title="Организация" id="orgSelectForStudents"
          @input="orgChanged" class="select-org mt-2"></b-form-select>
      </div>
    </div>
    <div v-else>
      <h3 class="d-inline">
        <em class="far fa-1x fa-calendar"></em> Мероприятия
      </h3>
      <b-button variant="outline-primary" class="ml-3 mb-2" @click="addEvent">
        <em class="fas fa-plus mr-1"></em> Добавить мероприятие
      </b-button>
      <content-types
        @filter="getFilteredData"
        :disable="!loadedBefore"
        :types="isClassEventsPageByTeacher ? teacherTypes : cmTypes"
      ></content-types>
    </div>
    <div v-if="events && events.length && (loadedBefore || page > 1)">
      <b-card-group
        deck
        :class="'mb-' + countInRow"
        v-for="iter in Math.ceil(events.length / countInRow)"
        :key="iter"
      >
        <b-card
          :class="windowWidth < widthForChanges ? 'mb-3' : ''"
          v-for="event in events.slice(
            0 + countInRow * (iter - 1),
            countInRow + countInRow * (iter - 1)
          )"
          :key="event.id"
          :img-src="
            event.avatar
              ? '/api/files/' +
                event.files.find((p) => p.id === event.avatar).physicalName
              : defaultImage
          "
          img-alt=""
          img-top
          @click="goToEvent(event)"
          :style="{
            cursor: eventCardStyle.cursor,
            maxWidth: eventCardStyle.maxWidth,
            minWidth: eventCardStyle.minWidth,
          }"
        >
          {{ new Date(event.scheduleDate).toLocaleDateString() }}
          <router-link
            class="event__title"
            :to="event.id.toString()"
            append
            style="word-wrap: break-word; width: calc(100% - 40px)"
            >{{ event.title ? event.title : 'Название отсутствует' }}
          </router-link>
          <div class="event__statusBox">
            <b-badge :variant="$_contentMixin_getBadge(event.status)">
              {{ event.status }}
            </b-badge>
          </div>
        </b-card>
      </b-card-group>
    </div>
    <b-alert v-else-if="loadedBefore" show variant="info" class="mx-auto"
      >Мероприятия отсутствуют</b-alert
    >
    <template v-else>
      <skeleton-event :count="4"/>
    </template>
    <b-button
      variant="outline-primary"
      v-if="isMore"
      id="buttonShowMore"
      @click="showMore()"
      >Показать ещё мероприятия</b-button
    >
  </v-page-content>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import organisationAPI from '@/api/organization'
import { statusFilters, statusTranslates } from '@/config/content/statuses'
import { roles } from '@/config/roles'
import { typeTranslates } from '@/config/content/types'

import contentMixin from '@/components/helpers/mixins/content-mixin'
import SkeletonEvent from '@/components/ui/skeleton/SkeletonEvent.vue'

const ContentTypes = () => import('@/components/content/edit/ListGroupButtonContentTypes.vue')

export default {
  name: 'AllEvents',
  components: {
    SkeletonEvent,
    ContentTypes
  },
  props: {
    editMode: Boolean
  },
  mixins: [contentMixin],
  data() {
    return {
      page: 1,
      countInRow: 4,
      countRows: 3,
      widthForChanges: 767,
      isMore: false,
      defaultImage: '/images/w1400_1.jpg',
      filters: [
        {
          param: 'status',
          needle: [statusFilters.accepted]
        }
      ],
      windowWidth: null,
      loadedBefore: false,
      teacherTypes: [
        { name: 'все', text: 'Все' },
        { name: statusFilters.draft, text: statusTranslates.draft },
        { name: statusFilters.accepted, text: statusTranslates.accepted },
        { name: statusFilters.removed, text: statusTranslates.removed }
      ],
      cmTypes: [
        { name: 'все', text: 'Все' },
        { name: statusFilters.rejected, text: statusTranslates.rejected },
        { name: statusFilters.await, text: statusTranslates.await },
        { name: statusFilters.draft, text: statusTranslates.draft },
        { name: statusFilters.accepted, text: statusTranslates.accepted },
        { name: statusFilters.removed, text: statusTranslates.removed }
      ],
      selectedOrg: null,
      organizations: [],
      isLoading: true
    }
  },
  beforeDestroy() {
    this.setEvents([])
  },
  async created() {
    if (this.hasRole('curator')) {
      const orgs = await organisationAPI.getClasses()
      this.organizations = Object.values(orgs.data)
      this.selectedOrg = this.optionsOrg[0].value
    }
    this.isLoading = false
    this.getData()
  },
  computed: {
    ...mapGetters({
      events: 'content/getEvents',
      currUser: 'currentUser/getCurrentUser',
      hasRole: 'currentUser/hasRole'
    }),
    eventCardStyle() {
      return {
        cursor: 'pointer',
        maxWidth:
          this.windowWidth < this.widthForChanges
            ? ''
            : 'calc((100% - 120px) / 4)',
        minWidth: this.windowWidth < this.widthForChanges ? '80%' : ''
      }
    },
    isClassEventsPage() {
      return this.$route.path.includes('class/content/events')
    },
    isClassEventsPageByTeacher() {
      return this.$route.path.includes('class/content/events') && this.hasRole(roles.teacher)
    },
    isClassEventsPageByCurator() {
      return this.$route.path.includes('class/content/events') && !this.$route.path.includes('teacher') && this.hasRole(roles.curator)
    },
    optionsOrg() {
      if (this.organizations && this.organizations.length) {
        const organizations = this.organizations.filter((p) => p.type === typeTranslates.class)
        if (this.hasRole(roles.curator) || this.hasRole(roles.admin)) {
          const options = []
          for (const organization of organizations) {
            const cityStr = (organization.city && !!organization.city.trim()) ? `${organization.city}, ` : ''
            options.push({
              value: organization.id,
              text: cityStr + organization.title
            })
          }
          return options
        } if (this.hasRole(roles.teacher)) {
          return [{
            value: this.currUser.organizationId,
            text: organizations.filter((p) => p.id === this.currUser.organizationId)[0].title
          }]
        }
      }
      return []
    }
  },
  beforeMount() {
    this.windowWidth = window.innerWidth
    window.onresize = () => {
      this.windowWidth = window.innerWidth
    }
  },
  methods: {
    ...mapActions({
      fetchEvents: 'content/fetchEvents',
      addContent: 'content/addContent',
      addContentByTeacher: 'content/addContentByTeacher'
    }),
    ...mapMutations({
      setEvents: 'content/setEvents'
    }),
    addEvent() {
      // добавляем контент, после - редирект на только что созданное мероприятие
      if (this.isClassEventsPageByTeacher) {
        this.addContentByTeacher(typeTranslates.event).then((res) => {
          this.$router.push(`/class/content/events/${res.contentObject.id}`)
        })
      } else {
        this.addContent(typeTranslates.event).then((res) => {
          this.$router.push(`/content/events/${res.contentObject.id}`)
        })
      }
    },
    goToEvent(event) {
      if (this.isClassEventsPageByTeacher || this.isClassEventsPage) {
        this.$router.push(`/class/content/events/${event.id}`)
      } else {
        this.$router.push(
          this.$route.path.includes('content')
            ? `/content/events/${event.id}`
            : `/news_feed/events/${event.id}`,
        )
      }
    },
    getFilters() {
      if (this.isClassEventsPageByCurator) {
        return [
          ...this.filters,
          {
            param: 'associatedOrgId',
            needle: [this.selectedOrg]
          }
        ]
      }
      if (this.isClassEventsPage) {
        return [
          ...this.filters,
          {
            param: 'associatedOrgId',
            needle: [this.currUser.organizationId]
          }
        ]
      }

      return this.filters
    },
    getData() {
      if (!this.editMode || this.isClassEventsPage) {
        this.fetchEvents({
          pages: {
            perPage: this.countInRow * this.countRows,
            pageNumber: this.page,
            filters: this.getFilters()
          },
          withoutClassEvents: !this.isClassEventsPage
        }).then((total) => {
          if (total > this.page * this.countInRow * this.countRows) {
            this.isMore = true
          }
          this.loadedBefore = true
        })
      }
    },
    getFilteredData(type) {
      this.isMore = false
      this.loadedBefore = false
      if (type === 'все') {
        this.filters[0].needle = [
          statusFilters.accepted,
          statusFilters.rejected,
          statusFilters.await,
          statusFilters.draft
        ]
      } else {
        this.filters[0].needle = [type]
      }
      this.page = 1
      this.fetchEvents({
        pages: {
          perPage: this.countInRow * this.countRows,
          pageNumber: this.page,
          filters: this.getFilters()
        },
        withoutClassEvents: !this.isClassEventsPage
      }).then((total) => {
        this.loadedBefore = true
        if (total > this.page * this.countInRow * this.countRows) {
          this.isMore = true
        }
      })
    },
    showMore() {
      this.loadedBefore = false
      this.page += 1
      this.fetchEvents({
        pages: {
          perPage: this.countInRow * this.countRows,
          pageNumber: this.page,
          filters: this.getFilters()
        },
        withoutClassEvents: !this.isClassEventsPage
      }).then((total) => {
        this.loadedBefore = true
        if (total <= this.page * this.countInRow * this.countRows) {
          this.isMore = false
        }
      })
    },
    orgChanged() {
      this.page = 1
      this.getData()
    }
  }
}
</script>

<style lang="scss" scoped>
#buttonShowMore {
  width: 100%;
}
.eventTile {
  float: left;
  width: 300px;
  padding: 20px;
}

.AllEvents__pageContent {
  max-width: 800px;
}

.event__statusBox {
  position: absolute;
  bottom: 5px;
}

.event__title {
  display: inline-block;
  margin-bottom: 20px !important;
}
</style>
